<template>
    <div class="form-edit-page">
        <div class="page-header">
            <div>
                <h1>Gestão de Processos por Município</h1>
                <p>
                    Acompanhamento centralizado de registros de estabelecimentos, produtos,
                    rótulos e processos vinculados ao departamento de inspeção.
                </p>
            </div>

            <div class="header-actions">
                <button class="btn btn-secondary">Exportar visão</button>
                <button class="btn btn-primary">Novo processo</button>
            </div>
        </div>

        <!-- FILTROS -->
        <div class="form-card">
            <div class="card-title">Filtros Gerenciais</div>

            <div class="row">
                <div class="field">
                    <label>Município</label>
                    <select v-model="filtros.idMunicipio">
                        <option value="">Todos</option>
                        <option
                            v-for="municipio in municipios"
                            :key="municipio.id"
                            :value="municipio.id"
                        >
                            {{ municipio.nome }}
                        </option>
                    </select>
                </div>

                <div class="field">
                    <label>Situação</label>
                    <select v-model="filtros.situacao">
                        <option value="">Todas</option>
                        <option value="EM_ANALISE">Em análise</option>
                        <option value="PENDENTE">Pendente</option>
                        <option value="DEFERIDO">Deferido</option>
                        <option value="INDEFERIDO">Indeferido</option>
                        <option value="CANCELADO">Cancelado</option>
                    </select>
                </div>

                <div class="field">
                    <label>Responsável</label>
                    <select v-model="filtros.responsavel">
                        <option value="">Todos</option>
                        <option
                            v-for="responsavel in responsaveis"
                            :key="responsavel"
                            :value="responsavel"
                        >
                            {{ responsavel }}
                        </option>
                    </select>
                </div>

                <div class="field field-grow">
                    <label>Pesquisa</label>
                    <input
                        v-model="filtros.texto"
                        type="text"
                        placeholder="Número, estabelecimento, produto, rótulo, município..."
                    />
                </div>
            </div>

            <div class="row row-actions">
                <button class="btn btn-primary" @click="aplicarFiltros">Atualizar painel</button>
                <button class="btn btn-secondary" @click="limparFiltros">Limpar filtros</button>
            </div>
        </div>

        <!-- KPI -->
        <div class="kpi-grid">
            <div class="kpi-card">
                <div class="kpi-title">Processos Totais</div>
                <div class="kpi-value">{{ indicadores.total }}</div>
                <div class="kpi-footer">Todos os tipos de registro</div>
            </div>

            <div class="kpi-card">
                <div class="kpi-title">Estabelecimentos</div>
                <div class="kpi-value">{{ indicadores.estabelecimentos }}</div>
                <div class="kpi-footer">Registros vinculados</div>
            </div>

            <div class="kpi-card">
                <div class="kpi-title">Produtos</div>
                <div class="kpi-value">{{ indicadores.produtos }}</div>
                <div class="kpi-footer">Cadastros / análises</div>
            </div>

            <div class="kpi-card">
                <div class="kpi-title">Rótulos</div>
                <div class="kpi-value">{{ indicadores.rotulos }}</div>
                <div class="kpi-footer">Solicitações vinculadas</div>
            </div>

            <div class="kpi-card kpi-attention">
                <div class="kpi-title">Pendências</div>
                <div class="kpi-value">{{ indicadores.pendencias }}</div>
                <div class="kpi-footer">Itens aguardando ação</div>
            </div>
        </div>

        <!-- RESUMO POR MUNICÍPIO -->
        <div class="form-card">
            <div class="card-title">Resumo por Município</div>

            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Município</th>
                            <th>Total</th>
                            <th>Em análise</th>
                            <th>Pendentes</th>
                            <th>Deferidos</th>
                            <th>Indeferidos</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in resumoPorMunicipio"
                            :key="item.municipio"
                            class="row-clickable"
                        >
                            <td>{{ item.municipio }}</td>
                            <td>{{ item.total }}</td>
                            <td>{{ item.emAnalise }}</td>
                            <td>{{ item.pendentes }}</td>
                            <td>{{ item.deferidos }}</td>
                            <td>{{ item.indeferidos }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ABAS -->
        <div class="tabs-bar">
            <button
                class="tab-button"
                :class="{ active: abaAtiva === 'visao-geral' }"
                @click="abaAtiva = 'visao-geral'"
            >
                Visão Geral
            </button>

            <button
                class="tab-button"
                :class="{ active: abaAtiva === 'estabelecimentos' }"
                @click="abaAtiva = 'estabelecimentos'"
            >
                Estabelecimentos
            </button>

            <button
                class="tab-button"
                :class="{ active: abaAtiva === 'produtos' }"
                @click="abaAtiva = 'produtos'"
            >
                Produtos
            </button>

            <button
                class="tab-button"
                :class="{ active: abaAtiva === 'rotulos' }"
                @click="abaAtiva = 'rotulos'"
            >
                Rótulos
            </button>

            <button
                class="tab-button"
                :class="{ active: abaAtiva === 'pendencias' }"
                @click="abaAtiva = 'pendencias'"
            >
                Pendências
            </button>

            <button
                class="tab-button"
                :class="{ active: abaAtiva === 'tramites' }"
                @click="abaAtiva = 'tramites'"
            >
                Tramitações
            </button>
        </div>

        <!-- ABA VISÃO GERAL -->
        <div class="form-card" v-if="abaAtiva === 'visao-geral'">
            <div class="card-title">Painel Consolidado</div>

            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Município</th>
                            <th>Tipo</th>
                            <th>Nº Processo</th>
                            <th>Estabelecimento</th>
                            <th>Referência</th>
                            <th>Situação</th>
                            <th>Responsável</th>
                            <th>Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in listaFiltrada"
                            :key="item.id"
                            @click="selecionarItem(item)"
                            :class="{ selected: itemSelecionado && itemSelecionado.id === item.id }"
                        >
                            <td>{{ item.municipio }}</td>
                            <td>{{ formatarTipo(item.tipo) }}</td>
                            <td>{{ item.numeroProcesso }}</td>
                            <td>{{ item.estabelecimento }}</td>
                            <td>{{ item.referencia }}</td>
                            <td>
                                <span class="status-pill" :class="statusClass(item.situacao)">
                                    {{ formatarSituacao(item.situacao) }}
                                </span>
                            </td>
                            <td>{{ item.responsavel }}</td>
                            <td>{{ item.data }}</td>
                        </tr>

                        <tr v-if="listaFiltrada.length === 0">
                            <td colspan="8" class="empty-row">
                                Nenhum processo encontrado para os filtros aplicados.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ABA ESTABELECIMENTOS -->
        <div class="form-card" v-if="abaAtiva === 'estabelecimentos'">
            <div class="card-title">Registros de Estabelecimentos</div>

            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Município</th>
                            <th>Nº Processo</th>
                            <th>Estabelecimento</th>
                            <th>Situação</th>
                            <th>Responsável</th>
                            <th>Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in listaEstabelecimentos"
                            :key="item.id"
                            @click="selecionarItem(item)"
                            :class="{ selected: itemSelecionado && itemSelecionado.id === item.id }"
                        >
                            <td>{{ item.municipio }}</td>
                            <td>{{ item.numeroProcesso }}</td>
                            <td>{{ item.estabelecimento }}</td>
                            <td>
                                <span class="status-pill" :class="statusClass(item.situacao)">
                                    {{ formatarSituacao(item.situacao) }}
                                </span>
                            </td>
                            <td>{{ item.responsavel }}</td>
                            <td>{{ item.data }}</td>
                        </tr>

                        <tr v-if="listaEstabelecimentos.length === 0">
                            <td colspan="6" class="empty-row">
                                Nenhum registro de estabelecimento encontrado.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ABA PRODUTOS -->
        <div class="form-card" v-if="abaAtiva === 'produtos'">
            <div class="card-title">Registros de Produtos</div>

            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Município</th>
                            <th>Nº Processo</th>
                            <th>Estabelecimento</th>
                            <th>Produto</th>
                            <th>Situação</th>
                            <th>Responsável</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in listaProdutos"
                            :key="item.id"
                            @click="selecionarItem(item)"
                            :class="{ selected: itemSelecionado && itemSelecionado.id === item.id }"
                        >
                            <td>{{ item.municipio }}</td>
                            <td>{{ item.numeroProcesso }}</td>
                            <td>{{ item.estabelecimento }}</td>
                            <td>{{ item.referencia }}</td>
                            <td>
                                <span class="status-pill" :class="statusClass(item.situacao)">
                                    {{ formatarSituacao(item.situacao) }}
                                </span>
                            </td>
                            <td>{{ item.responsavel }}</td>
                        </tr>

                        <tr v-if="listaProdutos.length === 0">
                            <td colspan="6" class="empty-row">
                                Nenhum registro de produto encontrado.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ABA RÓTULOS -->
        <div class="form-card" v-if="abaAtiva === 'rotulos'">
            <div class="card-title">Registros de Rótulos</div>

            <div class="table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Município</th>
                            <th>Nº Processo</th>
                            <th>Estabelecimento</th>
                            <th>Rótulo</th>
                            <th>Situação</th>
                            <th>Responsável</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in listaRotulos"
                            :key="item.id"
                            @click="selecionarItem(item)"
                            :class="{ selected: itemSelecionado && itemSelecionado.id === item.id }"
                        >
                            <td>{{ item.municipio }}</td>
                            <td>{{ item.numeroProcesso }}</td>
                            <td>{{ item.estabelecimento }}</td>
                            <td>{{ item.referencia }}</td>
                            <td>
                                <span class="status-pill" :class="statusClass(item.situacao)">
                                    {{ formatarSituacao(item.situacao) }}
                                </span>
                            </td>
                            <td>{{ item.responsavel }}</td>
                        </tr>

                        <tr v-if="listaRotulos.length === 0">
                            <td colspan="6" class="empty-row">
                                Nenhum registro de rótulo encontrado.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ABA PENDÊNCIAS -->
        <div class="form-card" v-if="abaAtiva === 'pendencias'">
            <div class="card-title">Pendências Operacionais</div>

            <div class="pendencias-grid">
                <div
                    class="pendencia-card"
                    v-for="item in listaPendencias"
                    :key="item.id"
                    @click="selecionarItem(item)"
                >
                    <div class="pendencia-top">
                        <span class="pendencia-municipio">{{ item.municipio }}</span>
                        <span class="status-pill" :class="statusClass(item.situacao)">
                            {{ formatarSituacao(item.situacao) }}
                        </span>
                    </div>

                    <div class="pendencia-title">
                        {{ item.numeroProcesso }} - {{ item.estabelecimento }}
                    </div>

                    <div class="pendencia-ref">
                        {{ formatarTipo(item.tipo) }} | {{ item.referencia }}
                    </div>

                    <div class="pendencia-text">
                        {{ item.pendencias }}
                    </div>

                    <div class="pendencia-footer">
                        Responsável: {{ item.responsavel }}
                    </div>
                </div>

                <div v-if="listaPendencias.length === 0" class="empty-panel">
                    Não há pendências para os filtros selecionados.
                </div>
            </div>
        </div>

        <!-- ABA TRAMITAÇÕES -->
        <div class="form-card" v-if="abaAtiva === 'tramites'">
            <div class="card-title">Tramitações e Histórico</div>

            <div class="timeline-big">
                <div
                    class="timeline-big-item"
                    v-for="item in listaFiltrada"
                    :key="item.id"
                    @click="selecionarItem(item)"
                >
                    <div class="timeline-big-header">
                        <div>
                            <strong>{{ item.numeroProcesso }}</strong>
                            <span class="timeline-tag">{{ formatarTipo(item.tipo) }}</span>
                        </div>
                        <span>{{ item.municipio }}</span>
                    </div>

                    <div class="timeline-big-sub">
                        {{ item.estabelecimento }} - {{ item.referencia }}
                    </div>

                    <div class="timeline-mini">
                        <div
                            class="timeline-mini-item"
                            v-for="(hist, index) in item.historico"
                            :key="index"
                        >
                            <div class="timeline-mini-date">{{ hist.data }}</div>
                            <div class="timeline-mini-content">
                                <strong>{{ hist.titulo }}</strong>
                                <p>{{ hist.descricao }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="listaFiltrada.length === 0" class="empty-panel">
                    Não há tramitações para os filtros selecionados.
                </div>
            </div>
        </div>

        <!-- DETALHE -->
        <div class="form-card" v-if="itemSelecionado">
            <div class="card-title">Detalhamento do Item Selecionado</div>

            <div class="detail-grid">
                <div class="detail-box">
                    <div class="detail-label">Município</div>
                    <div class="detail-value">{{ itemSelecionado.municipio }}</div>
                </div>

                <div class="detail-box">
                    <div class="detail-label">Tipo</div>
                    <div class="detail-value">{{ formatarTipo(itemSelecionado.tipo) }}</div>
                </div>

                <div class="detail-box">
                    <div class="detail-label">Número do Processo</div>
                    <div class="detail-value">{{ itemSelecionado.numeroProcesso }}</div>
                </div>

                <div class="detail-box">
                    <div class="detail-label">Situação</div>
                    <div class="detail-value">{{ formatarSituacao(itemSelecionado.situacao) }}</div>
                </div>

                <div class="detail-box">
                    <div class="detail-label">Estabelecimento</div>
                    <div class="detail-value">{{ itemSelecionado.estabelecimento }}</div>
                </div>

                <div class="detail-box">
                    <div class="detail-label">Referência</div>
                    <div class="detail-value">{{ itemSelecionado.referencia }}</div>
                </div>

                <div class="detail-box">
                    <div class="detail-label">Responsável</div>
                    <div class="detail-value">{{ itemSelecionado.responsavel }}</div>
                </div>

                <div class="detail-box">
                    <div class="detail-label">Data</div>
                    <div class="detail-value">{{ itemSelecionado.data }}</div>
                </div>
            </div>

            <div class="row">
                <div class="field field-grow">
                    <label>Pendências</label>
                    <textarea v-model="itemSelecionado.pendencias" rows="4" readonly />
                </div>
            </div>

            <div class="row">
                <div class="field field-grow">
                    <label>Observações</label>
                    <textarea v-model="itemSelecionado.observacoes" rows="4" readonly />
                </div>
            </div>

            <div class="subsection">
                <h3>Histórico de Tramitação</h3>
                <div class="timeline">
                    <div
                        class="timeline-item"
                        v-for="(hist, index) in itemSelecionado.historico"
                        :key="index"
                    >
                        <div class="timeline-date">{{ hist.data }}</div>
                        <div class="timeline-content">
                            <strong>{{ hist.titulo }}</strong>
                            <p>{{ hist.descricao }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'

const abaAtiva = ref('visao-geral')

const municipios = ref([
    { id: 1, nome: 'Araranguá' },
    { id: 2, nome: 'Sombrio' },
    { id: 3, nome: 'Balneário Arroio do Silva' },
    { id: 4, nome: 'Maracajá' },
    { id: 5, nome: 'Turvo' }
])

const responsaveis = ref([
    'Departamento de Inspeção',
    'Fiscal Simone',
    'Fiscal Marcos',
    'Supervisão Regional',
    'Equipe Técnica'
])

const filtros = reactive({
    idMunicipio: '',
    situacao: '',
    responsavel: '',
    texto: ''
})

const lista = ref([
    {
        id: 1,
        municipioId: 1,
        municipio: 'Araranguá',
        tipo: 'ESTABELECIMENTO',
        numeroProcesso: 'PROC-2026/0012',
        estabelecimento: 'Agroindústria Sabor Colonial',
        referencia: 'Registro inicial do estabelecimento',
        situacao: 'EM_ANALISE',
        responsavel: 'Departamento de Inspeção',
        data: '18/03/2026',
        pendencias: 'Apresentar ART atualizada e complementar o mapa de produção.',
        observacoes: 'Processo protocolado via portal, aguardando análise técnica final.',
        historico: [
            { data: '15/03/2026 09:10', titulo: 'Protocolo', descricao: 'Solicitação registrada pelo estabelecimento.' },
            { data: '16/03/2026 14:25', titulo: 'Triagem', descricao: 'Conferência preliminar da documentação.' },
            { data: '18/03/2026 10:40', titulo: 'Pendência', descricao: 'Necessária complementação documental.' }
        ]
    },
    {
        id: 2,
        municipioId: 2,
        municipio: 'Sombrio',
        tipo: 'PRODUTO',
        numeroProcesso: 'PROC-2026/0031',
        estabelecimento: 'Laticínios Serra Sul',
        referencia: 'Queijo Colonial Tradicional',
        situacao: 'PENDENTE',
        responsavel: 'Fiscal Simone',
        data: '17/03/2026',
        pendencias: 'Ajustar composição e anexar memorial descritivo.',
        observacoes: 'Produto vinculado a estabelecimento ativo no município.',
        historico: [
            { data: '17/03/2026 08:30', titulo: 'Cadastro', descricao: 'Produto submetido ao setor técnico.' },
            { data: '17/03/2026 16:05', titulo: 'Pendência técnica', descricao: 'Solicitado ajuste de formulação.' }
        ]
    },
    {
        id: 3,
        municipioId: 1,
        municipio: 'Araranguá',
        tipo: 'ROTULO',
        numeroProcesso: 'PROC-2026/0040',
        estabelecimento: 'Apiário Vale do Mel',
        referencia: 'Rótulo Mel Silvestre 500g',
        situacao: 'DEFERIDO',
        responsavel: 'Fiscal Marcos',
        data: '12/03/2026',
        pendencias: 'Sem pendências.',
        observacoes: 'Rótulo aprovado após conferência dos dizeres legais.',
        historico: [
            { data: '10/03/2026 11:20', titulo: 'Envio', descricao: 'Rótulo encaminhado pelo portal.' },
            { data: '11/03/2026 15:45', titulo: 'Análise', descricao: 'Conferência do layout e das informações obrigatórias.' },
            { data: '12/03/2026 09:15', titulo: 'Deferimento', descricao: 'Rótulo aprovado para uso.' }
        ]
    },
    {
        id: 4,
        municipioId: 3,
        municipio: 'Balneário Arroio do Silva',
        tipo: 'PROCESSO_ADMINISTRATIVO',
        numeroProcesso: 'PROC-2026/0055',
        estabelecimento: 'Pescados Costa Azul',
        referencia: 'Regularização documental',
        situacao: 'INDEFERIDO',
        responsavel: 'Supervisão Regional',
        data: '14/03/2026',
        pendencias: 'Documentação incompatível com a atividade requerida.',
        observacoes: 'Processo encerrado com decisão desfavorável.',
        historico: [
            { data: '09/03/2026 13:40', titulo: 'Abertura', descricao: 'Processo administrativo instaurado.' },
            { data: '12/03/2026 10:00', titulo: 'Parecer', descricao: 'Emitido parecer técnico desfavorável.' },
            { data: '14/03/2026 17:10', titulo: 'Encerramento', descricao: 'Processo indeferido.' }
        ]
    },
    {
        id: 5,
        municipioId: 4,
        municipio: 'Maracajá',
        tipo: 'ESTABELECIMENTO',
        numeroProcesso: 'PROC-2026/0061',
        estabelecimento: 'Embutidos Campo Nobre',
        referencia: 'Ampliação de registro de unidade',
        situacao: 'PENDENTE',
        responsavel: 'Equipe Técnica',
        data: '19/03/2026',
        pendencias: 'Enviar layout atualizado da unidade e licença sanitária.',
        observacoes: 'Ampliação solicitada com documentação parcial.',
        historico: [
            { data: '19/03/2026 08:00', titulo: 'Protocolo', descricao: 'Pedido de ampliação registrado.' },
            { data: '19/03/2026 14:10', titulo: 'Conferência inicial', descricao: 'Identificada ausência de documentos obrigatórios.' }
        ]
    },
    {
        id: 6,
        municipioId: 5,
        municipio: 'Turvo',
        tipo: 'ROTULO',
        numeroProcesso: 'PROC-2026/0068',
        estabelecimento: 'Doces Coloniais Turvo',
        referencia: 'Rótulo Doce de Leite Cremoso',
        situacao: 'EM_ANALISE',
        responsavel: 'Departamento de Inspeção',
        data: '20/03/2026',
        pendencias: 'Validar tabela nutricional e prazo de validade.',
        observacoes: 'Análise em andamento no setor de inspeção.',
        historico: [
            { data: '20/03/2026 09:20', titulo: 'Protocolo', descricao: 'Rótulo protocolado no sistema.' },
            { data: '20/03/2026 10:05', titulo: 'Distribuição', descricao: 'Encaminhado para análise técnica.' }
        ]
    }
])

const itemSelecionado = ref(lista.value[0] || null)

const listaFiltrada = computed(() => {
    return lista.value.filter(item => {
        const filtroMunicipio = !filtros.idMunicipio || item.municipioId === Number(filtros.idMunicipio)
        const filtroSituacao = !filtros.situacao || item.situacao === filtros.situacao
        const filtroResponsavel = !filtros.responsavel || item.responsavel === filtros.responsavel

        const texto = filtros.texto.trim().toLowerCase()
        const filtroTexto =
            !texto ||
            item.numeroProcesso.toLowerCase().includes(texto) ||
            item.estabelecimento.toLowerCase().includes(texto) ||
            item.referencia.toLowerCase().includes(texto) ||
            item.municipio.toLowerCase().includes(texto)

        return filtroMunicipio && filtroSituacao && filtroResponsavel && filtroTexto
    })
})

const listaEstabelecimentos = computed(() =>
    listaFiltrada.value.filter(item => item.tipo === 'ESTABELECIMENTO')
)

const listaProdutos = computed(() =>
    listaFiltrada.value.filter(item => item.tipo === 'PRODUTO')
)

const listaRotulos = computed(() =>
    listaFiltrada.value.filter(item => item.tipo === 'ROTULO')
)

const listaPendencias = computed(() =>
    listaFiltrada.value.filter(item => ['PENDENTE', 'EM_ANALISE'].includes(item.situacao))
)

const indicadores = computed(() => ({
    total: listaFiltrada.value.length,
    estabelecimentos: listaFiltrada.value.filter(item => item.tipo === 'ESTABELECIMENTO').length,
    produtos: listaFiltrada.value.filter(item => item.tipo === 'PRODUTO').length,
    rotulos: listaFiltrada.value.filter(item => item.tipo === 'ROTULO').length,
    pendencias: listaFiltrada.value.filter(item => ['PENDENTE', 'EM_ANALISE'].includes(item.situacao)).length
}))

const resumoPorMunicipio = computed(() => {
    const mapa = {}

    listaFiltrada.value.forEach(item => {
        if (!mapa[item.municipio]) {
            mapa[item.municipio] = {
                municipio: item.municipio,
                total: 0,
                emAnalise: 0,
                pendentes: 0,
                deferidos: 0,
                indeferidos: 0
            }
        }

        mapa[item.municipio].total += 1

        if (item.situacao === 'EM_ANALISE') mapa[item.municipio].emAnalise += 1
        if (item.situacao === 'PENDENTE') mapa[item.municipio].pendentes += 1
        if (item.situacao === 'DEFERIDO') mapa[item.municipio].deferidos += 1
        if (item.situacao === 'INDEFERIDO') mapa[item.municipio].indeferidos += 1
    })

    return Object.values(mapa)
})

function aplicarFiltros() {
    itemSelecionado.value = listaFiltrada.value[0] || null
}

function limparFiltros() {
    filtros.idMunicipio = ''
    filtros.situacao = ''
    filtros.responsavel = ''
    filtros.texto = ''
    itemSelecionado.value = lista.value[0] || null
}

function selecionarItem(item) {
    itemSelecionado.value = item
}

function formatarTipo(tipo) {
    const mapa = {
        ESTABELECIMENTO: 'Registro de Estabelecimento',
        PRODUTO: 'Registro de Produto',
        ROTULO: 'Registro de Rótulo',
        PROCESSO_ADMINISTRATIVO: 'Processo Administrativo'
    }
    return mapa[tipo] || tipo
}

function formatarSituacao(situacao) {
    const mapa = {
        EM_ANALISE: 'Em análise',
        PENDENTE: 'Pendente',
        DEFERIDO: 'Deferido',
        INDEFERIDO: 'Indeferido',
        CANCELADO: 'Cancelado'
    }
    return mapa[situacao] || situacao
}

function statusClass(situacao) {
    return {
        'status-analise': situacao === 'EM_ANALISE',
        'status-pendente': situacao === 'PENDENTE',
        'status-deferido': situacao === 'DEFERIDO',
        'status-indeferido': situacao === 'INDEFERIDO',
        'status-cancelado': situacao === 'CANCELADO'
    }
}
</script>

<style scoped>
.form-edit-page {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    background: #f5f7fb;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
}

.page-header h1 {
    margin: 0;
    font-size: 24px;
    color: #1f2937;
}

.page-header p {
    margin: 6px 0 0;
    color: #6b7280;
    max-width: 820px;
}

.header-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.form-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.card-title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 14px;
}

.row {
    display: flex;
    gap: 14px;
    margin-bottom: 14px;
    flex-wrap: wrap;
}

.row-actions {
    margin-bottom: 0;
}

.field {
    display: flex;
    flex-direction: column;
    min-width: 220px;
    flex: 1;
}

.field-grow {
    flex: 2;
}

.field label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.field input,
.field select,
.field textarea {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 14px;
    background: #fff;
    color: #111827;
    box-sizing: border-box;
}

.field textarea {
    resize: vertical;
}

.btn {
    border: none;
    border-radius: 10px;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.btn-primary {
    background: #2563eb;
    color: white;
}

.btn-secondary {
    background: #e5e7eb;
    color: #111827;
}

.kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
}

.kpi-card {
    background: #ffffff;
    border-radius: 14px;
    padding: 18px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.kpi-attention {
    border-color: #fbbf24;
}

.kpi-title {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;
}

.kpi-value {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
}

.kpi-footer {
    margin-top: 6px;
    font-size: 12px;
    color: #6b7280;
}

.tabs-bar {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.tab-button {
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    padding: 10px 16px;
    border-radius: 12px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
}

.tab-button.active {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
}

.table-wrapper {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table th {
    background: #f3f4f6;
    color: #374151;
    font-size: 13px;
    font-weight: 700;
    text-align: left;
    padding: 12px;
    border-bottom: 1px solid #e5e7eb;
}

.data-table td {
    padding: 12px;
    border-bottom: 1px solid #eef2f7;
    font-size: 14px;
    color: #111827;
}

.data-table tbody tr {
    cursor: pointer;
    transition: background 0.2s ease;
}

.data-table tbody tr:hover,
.row-clickable:hover {
    background: #f9fafb;
}

.data-table tbody tr.selected {
    background: #eff6ff;
}

.empty-row,
.empty-panel {
    text-align: center;
    color: #6b7280;
    padding: 20px;
}

.status-pill {
    display: inline-flex;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.status-analise {
    background: #dbeafe;
    color: #1d4ed8;
}

.status-pendente {
    background: #fef3c7;
    color: #b45309;
}

.status-deferido {
    background: #dcfce7;
    color: #15803d;
}

.status-indeferido {
    background: #fee2e2;
    color: #b91c1c;
}

.status-cancelado {
    background: #e5e7eb;
    color: #4b5563;
}

.pendencias-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
}

.pendencia-card {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 16px;
    background: #fafafa;
    cursor: pointer;
    transition: 0.2s ease;
}

.pendencia-card:hover {
    background: #f4f8ff;
    border-color: #bfdbfe;
}

.pendencia-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
}

.pendencia-municipio {
    font-size: 13px;
    font-weight: 700;
    color: #374151;
}

.pendencia-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 8px;
}

.pendencia-ref {
    font-size: 13px;
    color: #4b5563;
    margin-bottom: 10px;
}

.pendencia-text {
    font-size: 13px;
    color: #374151;
    line-height: 1.5;
}

.pendencia-footer {
    margin-top: 12px;
    font-size: 12px;
    color: #6b7280;
}

.timeline-big {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.timeline-big-item {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 16px;
    background: #fafafa;
    cursor: pointer;
}

.timeline-big-header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
    color: #111827;
    margin-bottom: 6px;
    flex-wrap: wrap;
}

.timeline-tag {
    margin-left: 10px;
    font-size: 12px;
    color: #2563eb;
    font-weight: 700;
}

.timeline-big-sub {
    font-size: 13px;
    color: #4b5563;
    margin-bottom: 12px;
}

.timeline-mini {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.timeline-mini-item {
    display: flex;
    gap: 12px;
    padding: 10px 12px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
}

.timeline-mini-date {
    min-width: 140px;
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.timeline-mini-content p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #374151;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;
}

.detail-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 12px;
}

.detail-label {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 4px;
}

.detail-value {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
}

.subsection {
    margin-top: 12px;
}

.subsection h3 {
    font-size: 15px;
    margin: 0 0 12px;
    color: #111827;
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.timeline-item {
    display: flex;
    gap: 12px;
    padding: 10px 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
}

.timeline-date {
    min-width: 130px;
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.timeline-content p {
    margin: 4px 0 0;
    color: #374151;
    font-size: 13px;
}

@media (max-width: 1300px) {
    .kpi-grid {
        grid-template-columns: repeat(3, 1fr);
    }

    .detail-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 900px) {
    .pendencias-grid {
        grid-template-columns: 1fr;
    }

    .kpi-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .kpi-grid,
    .detail-grid {
        grid-template-columns: 1fr;
    }

    .timeline-item,
    .timeline-mini-item {
        flex-direction: column;
    }

    .timeline-date,
    .timeline-mini-date {
        min-width: auto;
    }
}
</style>